import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

export const useScrollRestoration = (tabId = "") => {
  const location = useLocation();
  const scrollRestoredRef = useRef(false);

  // Restore scroll position when coming back from profile
  useEffect(() => {
    const isFromProfile = location.state?.fromProfileDetail === true;

    if (isFromProfile && !scrollRestoredRef.current) {
      const key = `scroll-${location.pathname}${tabId ? `-tab-${tabId}` : ""}`;
      const savedPosition = sessionStorage.getItem(key);

      if (savedPosition) {
        const scrollPos = parseInt(savedPosition, 10);

        // Mark that we're restoring
        scrollRestoredRef.current = true;

        // Multiple attempts to ensure scroll restoration works
        // Immediate
        window.scrollTo(0, scrollPos);

        // After a tick
        setTimeout(() => {
          window.scrollTo(0, scrollPos);
        }, 0);

        // After content likely loaded
        setTimeout(() => {
          window.scrollTo(0, scrollPos);
        }, 100);

        // Final attempt
        setTimeout(() => {
          window.scrollTo(0, scrollPos);
        }, 300);
      }
    }

    // Reset the flag when pathname OR tabId changes (if not from profile)
    return () => {
      if (!location.state?.fromProfileDetail) {
        scrollRestoredRef.current = false;
      }
    };
  }, [location.pathname, tabId, location.state?.fromProfileDetail]);

  // Save scroll position continuously
  useEffect(() => {
    const saveScrollPosition = () => {
      const key = `scroll-${location.pathname}${tabId ? `-tab-${tabId}` : ""}`;
      sessionStorage.setItem(key, String(window.scrollY));
    };

    // Save on scroll with debounce
    let timeoutId;
    const debouncedSave = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(saveScrollPosition, 150);
    };

    window.addEventListener("scroll", debouncedSave, { passive: true });

    return () => {
      window.removeEventListener("scroll", debouncedSave);
      clearTimeout(timeoutId);
      saveScrollPosition(); // Final save on unmount
    };
  }, [location.pathname, tabId]);
};
